import { ComponentProps } from 'react';

import { getRendererTypeFromQuestionType } from '@/shared-project-insights';
import {
  isVoiceAnswerable,
  RendererOption,
} from '@/shared-project-response-message-renderers';
import { Chat } from '@/shared-ui';
import {
  MessageSender,
  OptionRenderType,
  ProjectType,
  QuestionType,
} from '@/survey-graphql';

export const OPTIMISTIC_MESSAGE_TEMP_SENT_ID = 'temp-sent-id';
export const OPTIMISTIC_MESSAGE_TEMP_REPLY_ID = 'temp-reply-id';
export const FORMAT_TRANSCRIPT_ANSWER_TEMP_ID =
  'format-transcript-answer-temp-id';
export const V2_OPTIMISTIC_SENT_MESSAGE = {
  id: OPTIMISTIC_MESSAGE_TEMP_SENT_ID,
  text: '',
  content: '',
  createdAt: new Date().toISOString(),
  sender: MessageSender.User,
  optionRenderType: OptionRenderType.Emoji,
  assets: null,
  transcript: null,
  messageAssets: null,
  contentUrl: null,
  mobileAppEmbedUrl: null,
  mobileAppFileName: null,
  metadata: {
    isConversationOver: false,
    options: [],
    type: QuestionType.OpenEnded,
    required: true,
    maxSelections: null,
    assistantQuestion: false,
    questionId: null,
  },
};
export const V2_OPTIMISTIC_REPLY_MESSAGE = {
  id: OPTIMISTIC_MESSAGE_TEMP_REPLY_ID,
  text: '...',
  content: '',
  createdAt: new Date().toISOString(),
  sender: MessageSender.Assistant,
  optionRenderType: OptionRenderType.Emoji,
  assets: null,
  transcript: null,
  messageAssets: null,
  contentUrl: null,
  mobileAppEmbedUrl: null,
  mobileAppFileName: null,
  metadata: {
    isConversationOver: false,
    options: [],
    type: QuestionType.OpenEnded,
    required: true,
    maxSelections: null,
    assistantQuestion: false,
    questionId: null,
  },
};

const POLL_MESSAGE_UUID = '00000000-0000-0000-0000-000000000002';

/**
 * Narrow a globally flat message list to the one question session a message
 * belongs to.
 *
 * The V2 responder records a session per primary question and lets a respondent
 * jump back to any reached step and edit it, so a later-written message for an
 * earlier question sits next to a different question's assistant message. The
 * legacy responder got adjacency for free from strictly forward navigation;
 * `metadata.questionId` is the only linkage that survives back-navigation.
 *
 * Falls back to the full list when the message carries no question id (intro,
 * system and poll messages), which is also every legacy message's case.
 */
function scopeToQuestionSession<
  T extends { id: string; questionId?: string | null },
>(messageId: string | undefined, arr: T[]): T[] {
  const questionId = arr.find((m) => m.id === messageId)?.questionId;
  if (!questionId) return arr;
  return arr.filter((m) => m.questionId === questionId);
}

export function getAnsweredValueForAiMessage(
  message: ComponentProps<typeof Chat>['messages'][number],
  arr: {
    sender: 'user' | 'assistant' | 'system';
    text: string;
    id: string;
    questionId?: string | null;
  }[],
  hasQuestionSessions = false
) {
  if (message.id === POLL_MESSAGE_UUID) {
    const firstUserResponseIndex = arr.findIndex((m) => m.sender === 'user');
    if (firstUserResponseIndex !== -1) {
      return arr[firstUserResponseIndex].text;
    }
  }
  const scoped = hasQuestionSessions
    ? scopeToQuestionSession(message.id, arr)
    : arr;
  let nextMessageIndex = scoped.findIndex((m) => m.id === message.id) + 1;
  while (
    nextMessageIndex < scoped.length &&
    (scoped[nextMessageIndex].sender === 'assistant' ||
      scoped[nextMessageIndex].sender === 'system')
  ) {
    nextMessageIndex++;
  }
  if (nextMessageIndex < scoped.length) {
    const nextMessage = scoped[nextMessageIndex];
    return nextMessage.text;
  }
  return null;
}

export function getRelatedAiMessageToUserAnswer(
  currentIndex: number,
  arr: {
    sender: 'user' | 'assistant' | 'system';
    text: string;
    id: string;
    type?: string;
    optionRenderType: ChatMessageLike['optionRenderType'];
    options?: {
      label: string;
      value: string;
    }[];
    createdAt: Date | string | number;
    questionId?: string | null;
    subQuestions?: { id: string; text: string }[] | null;
  }[],
  hasQuestionSessions = false
) {
  const currentId = arr[currentIndex]?.id;
  const scoped = hasQuestionSessions
    ? scopeToQuestionSession(currentId, arr)
    : arr;
  const scopedIndex = hasQuestionSessions
    ? scoped.findIndex((m) => m.id === currentId)
    : currentIndex;

  let previousMessageIndex = scopedIndex - 1;
  while (
    previousMessageIndex >= 0 &&
    scoped[previousMessageIndex].sender === 'user'
  ) {
    previousMessageIndex--;
  }
  if (previousMessageIndex >= 0) {
    return scoped[previousMessageIndex];
  }
  return null;
}

export function getTypeOfRenderer(
  message: ComponentProps<typeof Chat>['messages'][number],
  projectType?: ProjectType
): RendererOption {
  // For UsabilityTesting projects, show Next button for URL/Task/Message questions
  if (projectType === ProjectType.UsabilityTesting) {
    const messageType = message.type?.toLowerCase();
    // Check if it's a URL question (has contentUrl)
    if (message.contentUrl) {
      return 'usabilityNext';
    }
    // Check if it's Task or Message question
    if (messageType === 'task' || messageType === 'message') {
      return 'usabilityNext';
    }
  }

  // AI Interview is delivered as ProjectType.Survey; its Message questions show the
  // same Next button as UT (no reply box). AI Survey uses the forms responder, not this.
  if (
    projectType === ProjectType.Survey &&
    message.type?.toLowerCase() === 'message'
  ) {
    return 'usabilityNext';
  }

  if (message.text && message.contentUrl) {
    return 'url';
  }
  return getRendererTypeFromQuestionType(
    message.type?.toLowerCase(),
    message.subQuestions?.length ?? 0
  );
}

type ChatMessageLike = ComponentProps<typeof Chat>['messages'][number];

export function isFollowUpQuestion(message: ChatMessageLike): boolean {
  return message.assistantQuestion ?? false;
}

export const DEFAULT_RENDERER_TYPE = 'openEnded';

export function hasSubmittableAnswer({
  content,
  transcript,
  rendererType,
}: {
  content: string | null | undefined;
  transcript: string | null | undefined;
  rendererType: RendererOption | null | undefined;
}): boolean {
  if (content?.trim()) return true;
  return isVoiceAnswerable(rendererType) && Boolean(transcript?.trim());
}
